// turnstile.js
'use strict';
import loadScriptTag from '../resource/loadScriptTag.js';
import { captcha } from '../resource/debug.js';
import env from '../resource/env.js';

const log = captcha.extend('log:turnstile');
const errorLog = captcha.extend('error:turnstile');

const SCRIPT_ID = 'turnstile-api-script-id';
export const TURNSTILE_CONTAINER_ID = 'turnstile-normal';
// https://developers.cloudflare.com/turnstile/frequently-asked-questions/#how-long-is-a-turnstile-token-valid-for-before-it-is-rejected-by-siteverify
// const TOKEN_REFRESH_TIMEOUT_MSEC = 300 * 1000 * 0.9;

const isProd = env.NODE_ENV === 'production';

let isTurnstileRemoved = false;

let waitingPromiseResolve;
const generateWaitingPromise = () =>
  new Promise(resolve => {
    waitingPromiseResolve = resolve;
  });
let isGeneratingTokenPromise;
let widgetId;

/**
 * Load Turnstile SDK script only (without rendering widget)
 * @return {Promise<void>}
 */
export const loadTurnstileScript = async () => {
  // if (!isProd) return;

  log('loadTurnstileScript');

  try {
    if (!document.getElementById(SCRIPT_ID)) {
      log('load turnstile script');
      await loadScriptTag({
        id: SCRIPT_ID,
        async: false,
        src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
      });

      if (window.turnstile) {
        await new Promise(resolve => window.turnstile.ready(resolve));
      } else {
        throw new Error('turnstile script error');
      }
      log('load turnstile script done');
    } else {
      log('turnstile script already loaded');
      // Ensure turnstile is ready
      if (window.turnstile) {
        await new Promise(resolve => window.turnstile.ready(resolve));
      }
    }
  } catch (e) {
    errorLog('loadTurnstileScript error', e);
    throw e;
  }
};

export const initTurnstile = async ({ sitekey, ...otherParams }) => {
  if (!isProd) return;

  log('initTurnstile', { sitekey, ...otherParams });

  try {
    if (!document.getElementById(SCRIPT_ID)) {
      log('load turnstile script');
      await loadScriptTag({
        id: SCRIPT_ID,
        async: false,
        src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
      });

      if (window.turnstile)
        await new Promise(resolve => window.turnstile.ready(resolve));
      else throw new Error('turnstile script error');
      log('load turnstile script done');
    }

    if ((window.turnstile && !widgetId) || isTurnstileRemoved) {
      log('render turnstile');
      isTurnstileRemoved = false;
      isGeneratingTokenPromise = generateWaitingPromise();
      widgetId = window.turnstile.render(`#${TURNSTILE_CONTAINER_ID}`, {
        sitekey,
        size: 'invisible',
        callback: token => {
          log('get token success', token);
          waitingPromiseResolve();
        },
        // use our own refresh mechanism to avoid unnecessary history entry
        'refresh-expired': 'never',
        'error-callback': error => {
          errorLog('error', error);
          isGeneratingTokenPromise = null;
          window.turnstile.remove();
          throw new Error(`turnstile error: error-callback ${error}`);
        },
        'unsupported-callback': reason => {
          errorLog('unsupported', reason);
          isGeneratingTokenPromise = null;
          window.turnstile.remove();
          throw new Error(`turnstile error: unsupported-callback ${reason}`);
        },
        ...otherParams,
      });
    }
  } catch (e) {
    errorLog('error', e);
    isGeneratingTokenPromise = null;
    window.turnstile?.remove();
  }
};

/**
 * Render a new Turnstile widget in a specific container
 * @param {Object} params
 * @param {string} params.containerId - The ID of the container element
 * @param {string} params.sitekey - Cloudflare Turnstile site key
 * @param {Function} params.onSuccess - Callback when verification succeeds
 * @param {Function} params.onError - Callback when verification fails
 * @param {Function} params.onExpired - Callback when token expires
 * @param {Function} params.onTimeout - Callback when verification times out
 * @return {string|null} Widget ID for future reference
 */
export const renderTurnstileWidget = async ({
  containerId,
  sitekey,
  onSuccess,
  onError,
  onExpired,
  onTimeout,
}) => {
  log('renderTurnstileWidget', { containerId, sitekey });

  try {
    await loadTurnstileScript();

    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container #${containerId} not found in DOM`);
    }

    const widgetId = window.turnstile.render(container, {
      sitekey,
      size: 'flexible',
      appearance: 'always',
      theme: 'light',
      'refresh-expired': 'auto',
      'response-field': false,
      callback: token => {
        log('widget verification success', { containerId, token });
        if (onSuccess) onSuccess(token);
      },
      'error-callback': error => {
        errorLog('widget error', { containerId, error });
        if (onError) onError(error);
      },
      'unsupported-callback': reason => {
        errorLog('widget unsupported', { containerId, reason });
        if (onError) onError(reason);
      },
      'expired-callback': () => {
        log('widget expired', { containerId });
        if (onExpired) onExpired();
      },
      'timeout-callback': () => {
        log('widget timeout', { containerId });
        if (onTimeout) onTimeout();
      },
    });

    log('widget rendered', { containerId, widgetId });

    return widgetId;
  } catch (error) {
    errorLog('renderTurnstileWidget error', error);
    if (onError) onError(error);
    return null;
  }
};

export const getTurnstileToken = async () => {
  let token;
  try {
    if (isGeneratingTokenPromise) await isGeneratingTokenPromise;

    token = window.turnstile?.getResponse();

    removeTurnstile();
    // eslint-disable-next-line no-empty
  } catch (e) {}

  return token;
};

// use remove instead of reset
// turnstile.reset will set iframe.src and web add an entry to history
// and then cause some chaos when navigating
const removeTurnstile = async () => {
  log('remove turnstile');

  window.turnstile?.remove();

  isTurnstileRemoved = true;
};
